import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Wrench, FileText, ChevronRight, ChevronDown, Plus, Upload, BarChart3, FileSpreadsheet, Loader2, Sparkles, X, FolderOpen, Trash2, Gavel, Globe, Link2, BarChart2, Folder, Star, Settings, Mail, Calendar, Video, Shield, Users, ExternalLink, LineChart } from 'lucide-react';
import { toast } from 'sonner';
import InterviewNotesModal from './InterviewNotesModal';
import DisciplinaryKeyInfoModal from './disciplinary/DisciplinaryKeyInfoModal';
import DisciplinaryViewerModal, { VIEWER_DRAFT_KEY as DISC_DRAFT_KEY } from './disciplinary/DisciplinaryViewerModal';
import DisciplinaryRecentsModal from './disciplinary/DisciplinaryRecentsModal';
import { hasPasswordSet, setPassword, checkPassword, saveRecent } from './disciplinary/disciplinaryStorage';
import GlobalUploadModal from './GlobalUploadModal';
import api from '../services/api';
import { API } from '../App';

const enconOrange = '#ee7203';

// Pre-loaded icon set (must match ManagementToolsSettings.jsx)
const PRELOADED_ICONS = {
  globe: Globe, link: Link2, chart: BarChart2, file: FileText, folder: Folder,
  star: Star, settings: Settings, mail: Mail, calendar: Calendar, video: Video,
  shield: Shield, users: Users,
};
const getPreloadedIcon = name => PRELOADED_ICONS[name] || Globe;

const ToolsDropdown = ({ user, isCurrentPage, mobileMode = false, inlineMenu = false, onClose }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showInterviewSub, setShowInterviewSub] = useState(false);
  const [showDiscSub, setShowDiscSub] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [generatingFromCv, setGeneratingFromCv] = useState(false);
  const [showCvDropzone, setShowCvDropzone] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [savedListLoading, setSavedListLoading] = useState(false);
  // Disciplinary tool state
  const [discKeyInfoOpen, setDiscKeyInfoOpen] = useState(false);
  const [discKeyInfoMode, setDiscKeyInfoMode] = useState('blank'); // 'blank' | 'upload'
  const [discBusy, setDiscBusy] = useState(false);
  const [discViewerOpen, setDiscViewerOpen] = useState(false);
  const [discData, setDiscData] = useState(null);
  const [discRecentsOpen, setDiscRecentsOpen] = useState(false);
  const [discPwPrompt, setDiscPwPrompt] = useState(null); // pending data to save once password set
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cvFileInputRef = useRef(null);

  const isSuperAdmin = user?.role === 'super_admin';
  // Helper: close this menu (and parent Admin dropdown when embedded)
  const closeMenu = () => {
    setShowDropdown(false);
    setShowInterviewSub(false);
    if (inlineMenu) onClose?.();
  };
  const [showGlobalUpload, setShowGlobalUpload] = useState(false);

  // Mobile accordion states
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileBumOpen, setMobileBumOpen] = useState(false);
  const [mobileInterviewOpen, setMobileInterviewOpen] = useState(false);
  const [mobileDiscOpen, setMobileDiscOpen] = useState(false);

  // Desktop BUM / Admin folder toggle
  const [showBumAdminFolder, setShowBumAdminFolder] = useState(false);

  // ── Management Tools permissions (which tabs / custom URLs this user can see)
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [customUrls, setCustomUrls] = useState([]);
  // Currently open custom-link modal (null when closed)
  const [openLink, setOpenLink] = useState(null);

  // Opens a super-admin-defined custom URL. Closes only the LOCAL dropdown UI
  // first — never the parent (`onClose`) — because when this component is
  // embedded inline in the Admin dropdown (`inlineMenu`), calling the parent's
  // onClose synchronously unmounts this whole component (and the modal we're
  // about to open) before it ever renders. The parent is only closed once the
  // modal itself is dismissed, in `handleCloseCustomUrlModal` below.
  // Before showing the iframe, we ask the backend whether the target site
  // even allows being embedded (X-Frame-Options / CSP frame-ancestors) — if
  // not, we skip the iframe entirely and open a real new tab automatically,
  // no click required.
  const handleOpenCustomUrl = async (u) => {
    setShowDropdown(false);
    setShowInterviewSub(false);
    setShowDiscSub(false);
    setShowBumAdminFolder(false);
    setMobileOpen(false);
    try {
      const res = await api.get(`${API}/management-tools/check-embeddable`, { params: { url: u.url } });
      if (res.data?.embeddable === false) {
        window.open(u.url, '_blank', 'noopener,noreferrer');
        toast.info(`${u.display_name} can't be embedded here — opened in a new tab`);
        closeMenu();
        return;
      }
    } catch (_) { /* on any check failure, fall through and try the iframe */ }
    setOpenLink(u);
  };

  const handleCloseCustomUrlModal = () => {
    setOpenLink(null);
    closeMenu();
  };

  // Belt-and-braces UX: the backend header check above catches the vast
  // majority of blocked embeds automatically (no click needed). A handful of
  // sites block framing via other means (JS frame-busting) that can't be
  // detected from outside the frame due to same-origin restrictions — for
  // those rare cases, show a small "open in a new tab" hint a few seconds
  // after the modal opens, rather than leaving someone staring at a blank box.
  const [showEmbedHint, setShowEmbedHint] = useState(false);
  useEffect(() => {
    if (!openLink) { setShowEmbedHint(false); return; }
    setShowEmbedHint(false);
    const t = setTimeout(() => setShowEmbedHint(true), 3500);
    return () => clearTimeout(t);
  }, [openLink]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`${API}/management-tools/my-permissions`);
        if (cancelled) return;
        setAllowedTabs(r.data?.tabs || []);
        setCustomUrls(r.data?.custom_urls || []);
      } catch (_) { /* silent — fall back to "no extras" */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const canSeeTab = (id) => isSuperAdmin || allowedTabs.includes(id);

  // Restore disciplinary draft from sessionStorage on mount so closing the
  // modal (or refreshing the dropdown) never loses anything.
  useEffect(() => {
    if (!isSuperAdmin) return;
    try {
      const saved = sessionStorage.getItem(DISC_DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.sections) setDiscData(parsed);
      }
    } catch (_) { /* noop */ }
  }, [isSuperAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowInterviewSub(false);
        setShowDiscSub(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNewNotes = () => {
    setModalData(null);
    setModalOpen(true);
    setShowDropdown(false);
    setShowInterviewSub(false);
  };

  const handleEditNotes = () => {
    fileInputRef.current?.click();
    setShowDropdown(false);
    setShowInterviewSub(false);
  };

  const handleNewFromCv = () => {
    setShowDropdown(false);
    setShowInterviewSub(false);
    setShowCvDropzone(true);
  };

  const handleOpenSavedList = async () => {
    setShowDropdown(false);
    setShowInterviewSub(false);
    setShowSavedList(true);
    setSavedListLoading(true);
    try {
      const res = await api.get(`${API}/interview-notes`);
      const items = res.data?.notes || res.data || [];
      setSavedList(Array.isArray(items) ? items : []);
    } catch (e) {
      console.warn('Failed to load saved notes:', e);
      toast.error('Failed to load saved notes');
      setSavedList([]);
    } finally {
      setSavedListLoading(false);
    }
  };

  const handleOpenSavedNote = async (note) => {
    try {
      // Fetch the full record so we get questions/notes/scores in full.
      const res = await api.get(`${API}/interview-notes/${note.id}`);
      const full = res.data || note;
      setModalData({
        id: full.id,
        candidate_name: full.candidate_name || '',
        date: full.date || '',
        interviewer: full.interviewer || '',
        questions: full.questions || {},
        custom_questions: full.custom_questions || null,
        cv_generated: !!full.custom_questions,
        summary: full.summary || {
          overall_impression: '', key_strengths: '', areas_of_concern: '', recommendation: '',
        },
      });
      setModalOpen(true);
      setShowSavedList(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to open note');
    }
  };

  const handleDeleteSavedNote = async (note) => {
    if (!window.confirm(`Delete note for ${note.candidate_name || 'this candidate'}?`)) return;
    try {
      await api.delete(`${API}/interview-notes/${note.id}`);
      setSavedList(prev => prev.filter(n => n.id !== note.id));
      toast.success('Note deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ===== Disciplinary Tool handlers =====
  const closeAllMenus = () => { setShowDropdown(false); setShowInterviewSub(false); setShowDiscSub(false); setShowBumAdminFolder(false); };

  const handleDiscNew = () => {
    closeAllMenus();
    if (discData && !window.confirm('You have a script in progress. Start a new one and discard the current draft?')) {
      // User chose not to discard — re-open the existing viewer instead.
      setDiscViewerOpen(true);
      return;
    }
    try { sessionStorage.removeItem(DISC_DRAFT_KEY); } catch (_) { /* noop */ }
    setDiscData(null);
    setDiscKeyInfoMode('blank');
    setDiscKeyInfoOpen(true);
  };
  const handleDiscFromUpload = () => {
    closeAllMenus();
    if (discData && !window.confirm('You have a script in progress. Start a new one and discard the current draft?')) {
      setDiscViewerOpen(true);
      return;
    }
    try { sessionStorage.removeItem(DISC_DRAFT_KEY); } catch (_) { /* noop */ }
    setDiscData(null);
    setDiscKeyInfoMode('upload');
    setDiscKeyInfoOpen(true);
  };
  const handleDiscRecents = () => {
    closeAllMenus();
    setDiscRecentsOpen(true);
  };
  const handleDiscContinueDraft = () => {
    closeAllMenus();
    setDiscViewerOpen(true);
  };

  // ===== Upload from saved PDF =====
  // Re-opens a previously generated disciplinary PDF in the editable
  // viewer. The PDF carries an embedded JSON payload (see
  // DisciplinaryViewerModal → handleExportPdf). We gate it behind the
  // user's CURRENT login password — verified server-side against the
  // hashed password — so a leaked PDF can't be silently re-opened.
  const savedPdfInputRef = useRef(null);
  const [pendingPdfFile, setPendingPdfFile] = useState(null);
  const [savedPdfPwModal, setSavedPdfPwModal] = useState(false);
  const [savedPdfPw, setSavedPdfPw] = useState('');
  const [savedPdfBusy, setSavedPdfBusy] = useState(false);

  const handleDiscFromSavedPdf = () => {
    closeAllMenus();
    if (discData && !window.confirm('You have a script in progress. Open the saved PDF and discard the current draft?')) {
      setDiscViewerOpen(true);
      return;
    }
    // Trigger the hidden file picker; once a file is chosen we open the
    // password modal.
    savedPdfInputRef.current?.click();
  };

  const handleSavedPdfPicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please choose a PDF generated by this tool');
      return;
    }
    setPendingPdfFile(file);
    setSavedPdfPw('');
    setSavedPdfPwModal(true);
  };

  const handleConfirmSavedPdfPw = async () => {
    if (!pendingPdfFile || !savedPdfPw) return;
    setSavedPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingPdfFile);
      fd.append('password', savedPdfPw);
      const res = await api.post(`${API}/tools/disciplinary/load-from-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const loaded = res.data?.data;
      if (!loaded || !loaded.sections) {
        toast.error('PDF did not contain disciplinary data');
        return;
      }
      // Reset any prior draft and open the viewer with the freshly-loaded
      // structure.
      try { sessionStorage.removeItem(DISC_DRAFT_KEY); } catch (_) { /* noop */ }
      setDiscData(loaded);
      setSavedPdfPwModal(false);
      setPendingPdfFile(null);
      setSavedPdfPw('');
      setDiscViewerOpen(true);
      toast.success('Loaded — make any further edits and re-download');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error('Incorrect login password');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load PDF');
      }
    } finally {
      setSavedPdfBusy(false);
    }
  };

  const handleDiscGenerate = async ({ keyInfo, file }) => {
    setDiscBusy(true);
    try {
      let res;
      if (discKeyInfoMode === 'upload' && file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('key_info', JSON.stringify(keyInfo));
        res = await api.post(`${API}/tools/disciplinary/generate-from-upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 90000,
        });
      } else {
        res = await api.post(`${API}/tools/disciplinary/generate-blank`, keyInfo);
      }
      setDiscData(res.data);
      setDiscKeyInfoOpen(false);
      setDiscViewerOpen(true);
      toast.success('Hearing script generated');
    } catch (e) {
      console.error('Disciplinary generate failed', e);
      toast.error(e?.response?.data?.detail || 'Failed to generate script');
    } finally {
      setDiscBusy(false);
    }
  };

  // The viewer auto-saves to sessionStorage as it edits; we just mirror
  // that into our own state so we never go out of sync.
  const handleDiscDataChange = (next) => setDiscData(next);

  const handleDiscSaveRecent = async (data) => {
    if (!hasPasswordSet()) {
      const pw = window.prompt('Set a session password for Recents (this is browser-tab-only — wipes when you close the tab):');
      if (!pw || pw.length < 4) {
        toast.error('Password must be at least 4 characters');
        return false;
      }
      setPassword(pw);
      saveRecent(pw, { ...data, saved_at: new Date().toISOString() });
      return true;
    }
    const pw = window.prompt('Enter your session password to save:');
    if (!pw) return false;
    if (!checkPassword(pw)) { toast.error('Incorrect password'); return false; }
    saveRecent(pw, { ...data, saved_at: new Date().toISOString() });
    return true;
  };

  const handleDiscOpenRecent = (item) => {
    setDiscData(item);
    setDiscRecentsOpen(false);
    setDiscViewerOpen(true);
  };

  const handleCvFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) {
      toast.error('Please upload a PDF or Word (.docx) CV');
      return;
    }
    setGeneratingFromCv(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/tools/generate-interview-from-cv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setModalData(res.data);
      setModalOpen(true);
      setShowCvDropzone(false);
      toast.success(`Tailored questions generated for ${res.data?.candidate_name || 'the candidate'}`);
    } catch (err) {
      console.error('CV → interview generation error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to generate questions from CV');
    } finally {
      setGeneratingFromCv(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) {
      toast.error('Please upload a PDF or Word (.docx) file');
      return;
    }

    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post(`${API}/tools/parse-interview-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setModalData(res.data);
      setModalOpen(true);
      toast.success('Document parsed successfully');
    } catch (err) {
      console.error('Parse error:', err);
      toast.error('Failed to parse document. Please check the file format.');
    } finally {
      setParsing(false);
      e.target.value = '';
    }
  };

  // ── Mobile accordion mode ────────────────────────────────────────────────────
  if (mobileMode) {
    return (
      <>
        <button
          onClick={() => setMobileOpen(o => !o)}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          data-testid="mobile-nav-mgmt-tools"
        >
          <Wrench className="w-5 h-5 flex-shrink-0" style={{ color: enconOrange }} />
          <span className="font-medium text-sm flex-1">Manager Admin</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
        </button>

        {mobileOpen && (
          <div className="ml-4 space-y-1">
            {/* Upload Data — prominent orange pill, always first */}
            {(user?.role === 'super_admin' || user?.can_access_global_upload) && (
              <button
                onClick={() => { setMobileOpen(false); setShowGlobalUpload(true); }}
                className="w-full flex items-center justify-center gap-2 p-2.5 rounded-full text-white font-semibold text-sm shadow-sm transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: enconOrange }}
                data-testid="mobile-tools-upload-data"
              >
                <Upload className="w-4 h-4 flex-shrink-0" />
                <span>Upload Data</span>
              </button>
            )}

            {/* BI Reports */}
            {(user?.role === 'super_admin' || user?.can_access_bi_reports) && (
              <button onClick={() => { setMobileOpen(false); navigate('/bi-reports'); }} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-bi-reports">
                <LineChart className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
                <span className="text-sm font-medium">BI Reports</span>
              </button>
            )}

            {/* BUM / Admin folder — clickable to expand/collapse */}
            {(canSeeTab('interview_notes') || (isSuperAdmin && canSeeTab('disciplinary'))) && (
              <>
                <button
                  onClick={() => setMobileBumOpen(o => !o)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
                  data-testid="mobile-tools-bum-admin"
                >
                  {mobileBumOpen
                    ? <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
                    : <Folder className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />}
                  <span className="text-sm font-semibold flex-1" style={{ color: enconOrange }}>BUM / Admin</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mobileBumOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
                </button>

                {mobileBumOpen && (
                  <div className="ml-4 space-y-1 border-l-2 pl-2" style={{ borderColor: `${enconOrange}40` }}>
                    {/* Interview Notes */}
                    {canSeeTab('interview_notes') && (
                      <>
                        <button
                          onClick={() => setMobileInterviewOpen(o => !o)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
                          data-testid="mobile-nav-interview-notes-expand"
                        >
                          <FileText className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
                          <span className="text-sm font-medium flex-1">Interview Notes</span>
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mobileInterviewOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
                        </button>
                        {mobileInterviewOpen && (
                          <div className="ml-4 space-y-1">
                            <button onClick={handleNewNotes} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-new-notes">
                              <Plus className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">New Notes</span>
                            </button>
                            <button onClick={handleNewFromCv} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-new-from-cv">
                              <FileText className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">{generatingFromCv ? 'Generating…' : 'New from CV'}</span>
                            </button>
                            <button onClick={handleEditNotes} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-edit-notes">
                              <Upload className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">{parsing ? 'Parsing...' : 'Edit Notes'}</span>
                            </button>
                            <button onClick={handleOpenSavedList} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-saved-notes">
                              <FolderOpen className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">Saved Notes</span>
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Disciplinary Questions */}
                    {isSuperAdmin && canSeeTab('disciplinary') && (
                      <>
                        <button
                          onClick={() => setMobileDiscOpen(o => !o)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left"
                          data-testid="mobile-nav-disc-expand"
                        >
                          <Gavel className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
                          <span className="text-sm font-medium flex-1">Disciplinary Questions</span>
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mobileDiscOpen ? 'rotate-180' : ''}`} style={{ color: enconOrange }} />
                        </button>
                        {mobileDiscOpen && (
                          <div className="ml-4 space-y-1">
                            {discData && (
                              <button onClick={handleDiscContinueDraft} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left">
                                <FileText className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm truncate">Continue: {discData.key_info?.employee_name || 'draft'}</span>
                              </button>
                            )}
                            <button onClick={handleDiscNew} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-disc-new">
                              <Plus className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">Create New</span>
                            </button>
                            <button onClick={handleDiscFromUpload} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left">
                              <Upload className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">Create from Upload</span>
                            </button>
                            <button onClick={handleDiscFromSavedPdf} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left">
                              <FileText className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">Upload from saved PDF</span>
                            </button>
                            <button onClick={handleDiscRecents} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left">
                              <FolderOpen className="w-3.5 h-3.5" style={{ color: enconOrange }} /><span className="text-sm">Recents</span>
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {canSeeTab('sdp_crm') && (
              <button onClick={() => navigate('/sdp-crm')} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-tools-sdp-crm">
                <FileSpreadsheet className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} />
                <span className="text-sm font-medium">SDP / CRM</span>
              </button>
            )}

            {customUrls.map(u => {
              const Icon = u.icon_type === 'custom' ? null : getPreloadedIcon(u.icon_name);
              return (
                <button key={u.id} onClick={() => handleOpenCustomUrl(u)} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 hover:bg-orange-50 transition-colors text-left">
                  {u.icon_type === 'custom' && u.icon_data ? <img src={u.icon_data} alt="" className="w-4 h-4 object-contain flex-shrink-0" /> : Icon ? <Icon className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} /> : null}
                  <span className="text-sm truncate flex-1">{u.display_name}</span>
                </button>
              );
            })}

            <button className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100 cursor-not-allowed text-left opacity-50">
              <BarChart3 className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-500">CRM Reporting</span><span className="text-xs text-gray-400 ml-auto">Soon</span>
            </button>
          </div>
        )}

        {/* Hidden file inputs */}
        <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc" onChange={handleFileUpload} className="hidden" />
        <input ref={cvFileInputRef} type="file" accept=".pdf,.docx" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleCvFile(f); }} className="hidden" />
        {showCvDropzone && <CvUploadModal onClose={() => !generatingFromCv && setShowCvDropzone(false)} onBrowse={() => cvFileInputRef.current?.click()} onDrop={handleCvFile} generating={generatingFromCv} />}
        {showSavedList && <SavedNotesModal notes={savedList} loading={savedListLoading} onClose={() => setShowSavedList(false)} onOpen={handleOpenSavedNote} onDelete={handleDeleteSavedNote} />}
        <InterviewNotesModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setModalData(null); }} initialData={modalData} user={user} />
        {isSuperAdmin && (
          <>
            <DisciplinaryKeyInfoModal isOpen={discKeyInfoOpen} mode={discKeyInfoMode} onClose={() => setDiscKeyInfoOpen(false)} onSubmit={handleDiscGenerate} busy={discBusy} />
            <DisciplinaryViewerModal isOpen={discViewerOpen} initialData={discData} onClose={() => setDiscViewerOpen(false)} onSaveRecent={handleDiscSaveRecent} onDataChange={handleDiscDataChange} />
            <DisciplinaryRecentsModal isOpen={discRecentsOpen} onClose={() => setDiscRecentsOpen(false)} onOpen={handleDiscOpenRecent} />
            <input ref={savedPdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="disc-saved-pdf-input" onChange={handleSavedPdfPicked} />
            {savedPdfPwModal && (
              <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { if (!savedPdfBusy) setSavedPdfPwModal(false); }} data-testid="disc-saved-pdf-pw-modal">
                <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" style={{ border: `3px solid ${enconOrange}` }}>
                  <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-lg font-extrabold text-gray-900">Confirm with login password</h3><p className="text-xs text-gray-500 mt-0.5">Re-opening a saved disciplinary PDF — enter the password you use to sign in.</p></div>
                  <div className="px-5 py-4 space-y-3">{pendingPdfFile && <p className="text-xs text-gray-600 truncate">File: <span className="font-semibold text-gray-800">{pendingPdfFile.name}</span></p>}<input type="password" value={savedPdfPw} onChange={(e) => setSavedPdfPw(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && savedPdfPw && !savedPdfBusy) handleConfirmSavedPdfPw(); }} placeholder="Login password" autoFocus className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200" data-testid="disc-saved-pdf-pw-input" /></div>
                  <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2"><button onClick={() => { setSavedPdfPwModal(false); setPendingPdfFile(null); setSavedPdfPw(''); }} disabled={savedPdfBusy} className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50" data-testid="disc-saved-pdf-pw-cancel">Cancel</button><button onClick={handleConfirmSavedPdfPw} disabled={savedPdfBusy || !savedPdfPw} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: enconOrange }} data-testid="disc-saved-pdf-pw-confirm">{savedPdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{savedPdfBusy ? 'Verifying…' : 'Open script'}</button></div>
                </div>
              </div>
            )}
          </>
        )}
        {openLink && createPortal((
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60" onClick={handleCloseCustomUrlModal} data-testid="custom-url-modal">
            <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: '95vw', height: '92vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
                <div className="flex items-center gap-2">{openLink.icon_type === 'custom' && openLink.icon_data ? (<img src={openLink.icon_data} alt="" className="w-5 h-5 object-contain" />) : (() => { const I = getPreloadedIcon(openLink.icon_name); return <I className="w-5 h-5 text-white" />; })()}<span className="font-bold text-white text-lg">{openLink.display_name}</span></div>
                <div className="flex items-center gap-1"><a href={openLink.url} target="_blank" rel="noopener noreferrer" className="text-white hover:bg-white/20 rounded-full p-1 transition-colors" title="Open in new tab" data-testid="custom-url-modal-open-new-tab"><ExternalLink className="w-5 h-5" /></a><button onClick={handleCloseCustomUrlModal} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors" data-testid="close-custom-url-modal"><X className="w-5 h-5" /></button></div>
              </div>
              <iframe src={openLink.url} className="flex-1 w-full border-0 rounded-b-xl" title={openLink.display_name} allow="clipboard-write" />
            </div>
          </div>
        ), document.body)}
      </>
    );
  }

  // ── Desktop pill mode (or inline-embedded in Admin dropdown) ──────────────────
  return (
    <>
      <div className={inlineMenu ? '' : 'relative'} ref={inlineMenu ? null : dropdownRef}>
        {/* Pill button — hidden when embedded in another dropdown */}
        {!inlineMenu && (
          <button
            data-testid="pill-tools"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDropdown(!showDropdown);
              if (showDropdown) setShowInterviewSub(false);
            }}
            className={`h-9 flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 rounded-full border-2 transition-all duration-200 whitespace-nowrap ${
              showDropdown ? 'shadow-lg' : 'bg-white hover:shadow-lg hover:scale-105'
            }`}
            style={{
              borderColor: enconOrange,
              backgroundColor: showDropdown ? enconOrange : 'white',
              position: 'relative',
              zIndex: showDropdown ? 51 : 'auto'
            }}
          >
            <Wrench className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: showDropdown ? 'white' : enconOrange }} />
            <span className="font-semibold text-[11px] lg:text-xs xl:text-sm whitespace-nowrap" style={{ color: showDropdown ? 'white' : enconOrange }}>Manager Admin</span>
            <ChevronDown
              className={`w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
              style={{ color: showDropdown ? 'white' : enconOrange }}
            />
          </button>
        )}

        {(showDropdown || inlineMenu) && (
          <div
            className={inlineMenu ? 'relative' : 'absolute left-1/2 -translate-x-1/2 mt-1 w-56 rounded-2xl shadow-xl overflow-visible z-50'}
            style={inlineMenu ? {} : {
              background: 'linear-gradient(180deg, rgba(45, 45, 45, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
              border: `2px solid ${enconOrange}`,
              backdropFilter: 'blur(10px)',
              top: '100%'
            }}
          >
            {/* Arrow (floating mode only) */}
            {!inlineMenu && (
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
                style={{
                  background: 'rgba(45, 45, 45, 0.98)',
                  borderLeft: `2px solid ${enconOrange}`,
                  borderTop: `2px solid ${enconOrange}`
                }}
              />
            )}
            <div className="relative">
              {/* ── Upload Data — prominent orange pill, always first ────── */}
              {/* Hidden when inlineMenu (embedded in the Admin dropdown) — AdminDropdownPill renders its own copy at the very top of that menu instead. */}
              {!inlineMenu && (user?.role === 'super_admin' || user?.can_access_global_upload) && (
                <div className="p-2.5 pb-1.5 rounded-t-xl">
                  <button
                    data-testid="tools-upload-data"
                    onClick={() => setShowGlobalUpload(true)}
                    className="w-full py-2.5 px-4 rounded-full flex items-center justify-center gap-2 text-white font-semibold text-sm shadow-md transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: enconOrange }}
                  >
                    <Upload className="w-4 h-4" />
                    Upload Data
                  </button>
                </div>
              )}

              {/* ── BI Reports ─── standalone pill in AdminDropdownPill already covers
                     the inline case (see M45NavPills.jsx); only render here when this
                     ToolsDropdown is NOT embedded inline, to avoid a duplicate entry ── */}
              {!inlineMenu && (user?.role === 'super_admin' || user?.can_access_bi_reports) && (
                <button
                  data-testid="tools-bi-reports"
                  onClick={() => { closeMenu(); navigate('/bi-reports'); }}
                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                >
                  <LineChart className="w-4 h-4" style={{ color: enconOrange }} />
                  <span className="font-medium">BI Reports</span>
                </button>
              )}

              {/* ── BUM / Admin folder — clickable to expand/collapse ────── */}
              {(canSeeTab('interview_notes') || (isSuperAdmin && canSeeTab('disciplinary'))) && (
                <>
                  <button
                    data-testid="tools-bum-admin-folder"
                    onClick={() => setShowBumAdminFolder(v => !v)}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center justify-between transition-colors border-t border-white/10"
                  >
                    <div className="flex items-center gap-3">
                      {showBumAdminFolder
                        ? <FolderOpen className="w-4 h-4" style={{ color: enconOrange }} />
                        : <Folder className="w-4 h-4" style={{ color: enconOrange }} />}
                      <span className="font-semibold text-sm" style={{ color: enconOrange }}>BUM / Admin</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showBumAdminFolder ? 'rotate-180' : ''}`} />
                  </button>

                  {showBumAdminFolder && (
                    <div className="border-l-2 ml-3" style={{ borderColor: `${enconOrange}50` }}>
                      {/* Interview Notes - with sub-dropdown */}
                      {canSeeTab('interview_notes') && (
                      <div
                        className="relative"
                        onMouseEnter={() => setShowInterviewSub(true)}
                        onMouseLeave={() => setShowInterviewSub(false)}
                      >
                        <button
                          data-testid="tools-interview-notes"
                          onClick={() => setShowInterviewSub(!showInterviewSub)}
                          className="w-full px-4 py-2.5 text-left text-white hover:bg-white/15 flex items-center justify-between transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4" style={{ color: enconOrange }} />
                            <span className="font-medium">Interview Notes</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>

                        {/* Sub-dropdown */}
                        {showInterviewSub && (
                          <div
                            className="absolute right-full top-0 mr-1 w-48 rounded-xl shadow-xl overflow-hidden z-50"
                            style={{
                              background: 'linear-gradient(180deg, rgba(45, 45, 45, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
                              border: `2px solid ${enconOrange}`,
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            <button
                              data-testid="tools-new-notes"
                              onClick={handleNewNotes}
                              className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl"
                            >
                              <Plus className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="font-medium">New Notes</span>
                            </button>
                            <button
                              data-testid="tools-new-from-cv"
                              onClick={handleNewFromCv}
                              className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                            >
                              <FileText className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="font-medium">{generatingFromCv ? 'Generating…' : 'New Notes from CV'}</span>
                            </button>
                            <button
                              data-testid="tools-edit-notes"
                              onClick={handleEditNotes}
                              className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                            >
                              <Upload className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="font-medium">{parsing ? 'Parsing...' : 'Edit Notes'}</span>
                            </button>
                            <button
                              data-testid="tools-saved-notes"
                              onClick={handleOpenSavedList}
                              className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl"
                            >
                              <FolderOpen className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="font-medium">Saved Notes</span>
                            </button>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Disciplinary Questions — super admin only */}
                      {isSuperAdmin && canSeeTab('disciplinary') && (
                        <div
                          className="relative"
                          onMouseEnter={() => { setShowDiscSub(true); setShowInterviewSub(false); }}
                          onMouseLeave={() => setShowDiscSub(false)}
                        >
                          <button
                            data-testid="tools-disciplinary"
                            onClick={() => setShowDiscSub(!showDiscSub)}
                            className="w-full px-4 py-2.5 text-left text-white hover:bg-white/15 flex items-center justify-between transition-colors border-t border-white/10"
                          >
                            <div className="flex items-center gap-3">
                              <Gavel className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="font-medium">Disciplinary Questions</span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </button>

                          {showDiscSub && (
                            <div
                              className="absolute right-full top-0 mr-1 w-52 rounded-xl shadow-xl overflow-hidden z-50"
                              style={{
                                background: 'linear-gradient(180deg, rgba(45, 45, 45, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
                                border: `2px solid ${enconOrange}`,
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              {discData && (
                                <button
                                  data-testid="tools-disc-continue"
                                  onClick={handleDiscContinueDraft}
                                  className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl"
                                >
                                  <FileText className="w-4 h-4" style={{ color: enconOrange }} />
                                  <span className="font-medium truncate">Continue: {discData.key_info?.employee_name || 'draft'}</span>
                                </button>
                              )}
                              <button
                                data-testid="tools-disc-new"
                                onClick={handleDiscNew}
                                className={`w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors ${discData ? 'border-t border-white/10' : 'rounded-t-xl'}`}
                              >
                                <Plus className="w-4 h-4" style={{ color: enconOrange }} />
                                <span className="font-medium">Create New</span>
                              </button>
                              <button
                                data-testid="tools-disc-upload"
                                onClick={handleDiscFromUpload}
                                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                              >
                                <Upload className="w-4 h-4" style={{ color: enconOrange }} />
                                <span className="font-medium">Create from Upload</span>
                              </button>
                              <button
                                data-testid="tools-disc-from-saved-pdf"
                                onClick={handleDiscFromSavedPdf}
                                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                                title="Re-open a previously generated disciplinary PDF — asks for your login password"
                              >
                                <FileText className="w-4 h-4" style={{ color: enconOrange }} />
                                <span className="font-medium">Upload from saved PDF</span>
                              </button>
                              <button
                                data-testid="tools-disc-recents"
                                onClick={handleDiscRecents}
                                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl"
                              >
                                <FolderOpen className="w-4 h-4" style={{ color: enconOrange }} />
                                <span className="font-medium">Recents</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* SDP / CRM */}
              {canSeeTab('sdp_crm') && (
              <button
                data-testid="tools-sdp-report"
                onClick={() => {
                  navigate('/sdp-crm');
                  closeMenu();
                }}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
              >
                <FileSpreadsheet className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium">SDP / CRM</span>
              </button>
              )}

              {/* Custom URL links — super admin defined, per-user gated */}
              {customUrls.map((u) => {
                const Icon = u.icon_type === 'custom' ? null : getPreloadedIcon(u.icon_name);
                return (
                  <button
                    key={u.id}
                    type="button"
                    data-testid={`tools-custom-url-${u.id}`}
                    onClick={() => handleOpenCustomUrl(u)}
                    className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10"
                  >
                    {u.icon_type === 'custom' && u.icon_data
                      ? <img src={u.icon_data} alt="" className="w-4 h-4 object-contain flex-shrink-0" />
                      : Icon ? <Icon className="w-4 h-4 flex-shrink-0" style={{ color: enconOrange }} /> : null}
                    <span className="font-medium truncate flex-1">{u.display_name}</span>
                  </button>
                );
              })}

              {/* CRM Reporting - Placeholder */}
              <button
                data-testid="tools-crm-reporting"
                className="w-full px-4 py-3 text-left text-gray-500 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl cursor-not-allowed"
              >
                <BarChart3 className="w-4 h-4 text-gray-600" />
                <span className="font-medium">CRM Reporting</span>
                <span className="text-xs text-gray-600 ml-auto">Soon</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input — Edit Notes (parse existing interview file) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc"
        onChange={handleFileUpload}
        className="hidden"
        data-testid="upload-interview-file"
      />

      {/* Hidden file input — New Notes from CV (browse fallback) */}
      <input
        ref={cvFileInputRef}
        type="file"
        accept=".pdf,.docx"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleCvFile(f); }}
        className="hidden"
        data-testid="upload-cv-file"
      />

      {/* Drag-and-drop CV upload modal */}
      {showCvDropzone && (
        <CvUploadModal
          onClose={() => !generatingFromCv && setShowCvDropzone(false)}
          onBrowse={() => cvFileInputRef.current?.click()}
          onDrop={handleCvFile}
          generating={generatingFromCv}
        />
      )}

      {/* Saved Notes browser */}
      {showSavedList && (
        <SavedNotesModal
          notes={savedList}
          loading={savedListLoading}
          onClose={() => setShowSavedList(false)}
          onOpen={handleOpenSavedNote}
          onDelete={handleDeleteSavedNote}
        />
      )}

      {/* Interview Notes Modal */}
      <InterviewNotesModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setModalData(null); }}
        initialData={modalData}
        user={user}
      />

      {/* Disciplinary tool modals (super admin only) */}
      {isSuperAdmin && (
        <>
          <DisciplinaryKeyInfoModal
            isOpen={discKeyInfoOpen}
            mode={discKeyInfoMode}
            onClose={() => setDiscKeyInfoOpen(false)}
            onSubmit={handleDiscGenerate}
            busy={discBusy}
          />
          <DisciplinaryViewerModal
            isOpen={discViewerOpen}
            initialData={discData}
            onClose={() => setDiscViewerOpen(false)}
            onSaveRecent={handleDiscSaveRecent}
            onDataChange={handleDiscDataChange}
          />
          <DisciplinaryRecentsModal
            isOpen={discRecentsOpen}
            onClose={() => setDiscRecentsOpen(false)}
            onOpen={handleDiscOpenRecent}
          />

          {/* Hidden file input for the "Upload from saved PDF" flow. Triggered
              programmatically from the disciplinary submenu. */}
          <input
            ref={savedPdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="disc-saved-pdf-input"
            onChange={handleSavedPdfPicked}
          />

          {/* Login-password modal for re-opening a saved disciplinary PDF.
              Verified server-side against the user's hashed password —
              this is the same password used for /login, NOT the
              session-only Recents password. */}
          {savedPdfPwModal && (
            <div
              className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
              onClick={() => { if (!savedPdfBusy) setSavedPdfPwModal(false); }}
              data-testid="disc-saved-pdf-pw-modal"
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                style={{ border: `3px solid ${enconOrange}` }}
              >
                <div className="px-5 py-4 border-b border-gray-100">
                  <h3 className="text-lg font-extrabold text-gray-900">Confirm with login password</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Re-opening a saved disciplinary PDF — enter the password you use to sign in.
                  </p>
                </div>
                <div className="px-5 py-4 space-y-3">
                  {pendingPdfFile && (
                    <p className="text-xs text-gray-600 truncate">
                      File: <span className="font-semibold text-gray-800">{pendingPdfFile.name}</span>
                    </p>
                  )}
                  <input
                    type="password"
                    value={savedPdfPw}
                    onChange={(e) => setSavedPdfPw(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && savedPdfPw && !savedPdfBusy) handleConfirmSavedPdfPw(); }}
                    placeholder="Login password"
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                    data-testid="disc-saved-pdf-pw-input"
                  />
                </div>
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
                  <button
                    onClick={() => { setSavedPdfPwModal(false); setPendingPdfFile(null); setSavedPdfPw(''); }}
                    disabled={savedPdfBusy}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50"
                    data-testid="disc-saved-pdf-pw-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSavedPdfPw}
                    disabled={savedPdfBusy || !savedPdfPw}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                    style={{ backgroundColor: enconOrange }}
                    data-testid="disc-saved-pdf-pw-confirm"
                  >
                    {savedPdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {savedPdfBusy ? 'Verifying…' : 'Open script'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Custom URL modal — opens link inside an iframe (matches the
           New Accounts pill modal pattern). */}
      {openLink && createPortal((
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60"
          onClick={handleCloseCustomUrlModal}
          data-testid="custom-url-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl flex flex-col"
            style={{ width: '95vw', height: '92vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
              <div className="flex items-center gap-2">
                {openLink.icon_type === 'custom' && openLink.icon_data ? (
                  <img src={openLink.icon_data} alt="" className="w-5 h-5 object-contain" />
                ) : (() => {
                  const I = getPreloadedIcon(openLink.icon_name);
                  return <I className="w-5 h-5 text-white" />;
                })()}
                <span className="font-bold text-white text-lg">{openLink.display_name}</span>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={openLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                  title="Open in new tab"
                  data-testid="custom-url-modal-open-new-tab"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
                <button
                  onClick={handleCloseCustomUrlModal}
                  className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                  data-testid="close-custom-url-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {showEmbedHint && (
              <div
                className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800"
                data-testid="custom-url-embed-hint"
              >
                <span>Not displaying correctly? This tool may not allow embedding.</span>
                <a
                  href={openLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline whitespace-nowrap"
                  data-testid="custom-url-embed-hint-open-new-tab"
                >
                  Open in a new tab
                </a>
              </div>
            )}
            <iframe
              src={openLink.url}
              className="flex-1 w-full border-0 rounded-b-xl"
              title={openLink.display_name}
              allow="clipboard-write"
            />
          </div>
        </div>
      ), document.body)}

      {/* Global Upload Modal — triggered by Manager Admin > Upload Data.
          onClose also collapses the parent Admin dropdown (deferred so the
          inlineMenu-embedded instance isn't unmounted while the dialog is open). */}
      <GlobalUploadModal
        isOpen={showGlobalUpload}
        onClose={() => { setShowGlobalUpload(false); closeMenu(); }}
      />
    </>
  );
};

export default ToolsDropdown;

// ---------------------------------------------------------------------------

/**
 * Modal that lists every interview note autosaved to MongoDB so the user
 * can resume a previous session. Click a row to load it back into the main
 * Interview Notes modal — all notes, scores, and summary text restored.
 */
function SavedNotesModal({ notes, loading, onClose, onOpen, onDelete }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="saved-notes-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <FolderOpen className="w-5 h-5" style={{ color: enconOrange }} />
          <h3 className="text-lg font-extrabold text-gray-900">Saved Interview Notes</h3>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-700"
            data-testid="saved-notes-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading…
            </div>
          ) : notes.length === 0 ? (
            <div className="p-10 text-center">
              <FolderOpen className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No saved notes yet.</p>
              <p className="text-[11px] text-gray-400 mt-1">New interviews autosave automatically as you type.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {notes.map(n => (
                <li key={n.id} className="px-6 py-3 flex items-center gap-3 hover:bg-orange-50/50 transition-colors">
                  <button
                    type="button"
                    onClick={() => onOpen(n)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`saved-note-open-${n.id}`}
                  >
                    <div className="font-semibold text-sm text-gray-900 truncate">
                      {n.candidate_name || '(unnamed candidate)'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {n.date || '—'}{n.interviewer ? ` · ${n.interviewer}` : ''}
                      {n.updated_at ? ` · last edit ${new Date(n.updated_at).toLocaleString()}` : ''}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(n)}
                    className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded p-1.5 transition-colors"
                    title="Delete note"
                    data-testid={`saved-note-delete-${n.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Fullscreen drag-and-drop dropzone for the "New Notes from CV" flow.
 * Drop a .pdf / .docx anywhere on the dimmed background, or click Browse.
 */
function CvUploadModal({ onClose, onBrowse, onDrop, generating }) {
  const [isDragging, setIsDragging] = useState(false);
  const depthRef = useRef(0);
  const handleEnter = (e) => { e.preventDefault(); e.stopPropagation(); depthRef.current += 1; setIsDragging(true); };
  const handleLeave = (e) => { e.preventDefault(); e.stopPropagation(); depthRef.current = Math.max(0, depthRef.current - 1); if (depthRef.current === 0) setIsDragging(false); };
  const handleOver  = (e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'; };
  const handleDrop  = (e) => {
    e.preventDefault(); e.stopPropagation();
    depthRef.current = 0; setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onDrop(f);
  };
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      onDragEnter={handleEnter}
      onDragLeave={handleLeave}
      onDragOver={handleOver}
      onDrop={handleDrop}
      data-testid="cv-upload-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative bg-white rounded-3xl shadow-2xl border-4 border-dashed w-full max-w-xl p-10 text-center transition-colors ${
          isDragging ? 'border-orange-600 bg-orange-50' : 'border-orange-300'
        }`}
      >
        {!generating && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
            data-testid="cv-upload-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: '#fff1e0' }}>
          <Sparkles className="w-8 h-8" style={{ color: enconOrange }} />
        </div>
        <h3 className="text-lg font-extrabold text-gray-900 mb-1">
          {generating ? 'Reading the CV and tailoring questions…' : isDragging ? 'Drop the CV here' : 'New interview notes from a CV'}
        </h3>
        <p className="text-xs text-gray-500 max-w-sm mx-auto mb-5">
          Drop a candidate&apos;s <strong>.pdf</strong> or <strong>.docx</strong> CV anywhere on this screen. We&apos;ll generate a focused question list weighted to their most recent role — no company names, fully editable.
        </p>
        <button
          onClick={onBrowse}
          disabled={generating}
          className="inline-flex items-center gap-2 rounded-xl h-11 px-6 text-white font-semibold shadow-md disabled:opacity-60"
          style={{ backgroundColor: enconOrange }}
          data-testid="cv-upload-browse"
        >
          {generating
            ? <><Loader2 className="w-4 h-4 animate-spin" />Generating…</>
            : <><Upload className="w-4 h-4" />Browse files</>}
        </button>
        <div className="text-[11px] text-gray-400 mt-3">or drop the file anywhere on this screen</div>
      </div>
    </div>
  );
}
